import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import type { Producer, Transport } from 'mediasoup-client/lib/types'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { config } from '@/config/env'
import { useMeetingSessionStore, type RoomCredentials } from '../store/meeting-session.store'

function decodePeerIdFromToken(creds: RoomCredentials): string | null {
  try {
    const b64 = creds.roomToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(b64)) as Record<string, unknown>
    const id = payload.sub ?? payload.user_id
    if (typeof id === 'string' && id) return id
  } catch { /* ignore */ }
  // Session-stable UUID fallback when JWT decode fails
  const KEY = 'inmeet_guest_peer_id'
  const stored = sessionStorage.getItem(KEY)
  if (stored) return stored
  const fresh = crypto.randomUUID()
  sessionStorage.setItem(KEY, fresh)
  return fresh
}
import { scheduleRoomTokenRenewal } from '../services/room-token'
import { usePhoenixChannel } from './usePhoenixChannel'
import { usePresence } from './usePresence'
import { useActiveSpeakers } from './useActiveSpeakers'
import { useSfuJoin } from './useSfuJoin'
import { useMediaSessionHold } from './useMediaSessionHold'
import { useLocalMedia } from './useLocalMedia'
import { useConsumers, type AuxSource } from './useConsumers'
import { vclog, vcDebugEnabled } from './vc-log'
import { closeRemoteProducer } from '../services/producer-teardown'
import { ScreenSharePublisher } from '../services/screen-share-publisher'
import { mediaReports } from '../services/media-report'
import { useMeetingPreferencesStore } from '../store/meeting-preferences.store'
import { useDevicesStore, VIDEO_QUALITY_PRESETS } from '@/features/devices'
import { useMeetingLayoutStore } from '../store/meeting-layout.store'
import * as renderedTiles from '../services/rendered-tiles'
import * as fullscreenFocus from '../services/fullscreen-focus'
import * as layerPreference from '../services/layer-preference'
import { useParticipantVideoHiddenStore } from '../store/participant-video-hidden.store'
import { registerProducerStats, unregisterProducerStats } from '../services/rtp-stats'
import { produceScreenTrack } from '../services/screen-producer'

/** Hard cap on extra cameras per peer — 3 video producers including the primary. */
export const MAX_ADDITIONAL_VIDEO_INPUTS = 2

/** A locally-published extra camera. Mirrors `AuxSource` on the receiving side. */
export type LocalVideoInput = {
  /** Producer slot, 'aux1' | 'aux2'. Unique per peer, so it keys the tile. */
  sourceId: string
  deviceId: string
  label: string
  /**
   * The capture, or null while the input is added but not being sent — the
   * camera control is off, or it was added while it was off. An extra camera
   * follows the camera control: off means nothing of yours is transmitted,
   * whichever device it comes from.
   */
  stream: MediaStream | null
  /** True while this input is captured and published. */
  active: boolean
}

/**
 * A remote peer as rendered in the grid. The roster (who + name) comes from
 * Phoenix Presence; `stream`/`videoOn`/`muted` are attached from the SFU
 * consumer once its media arrives (null/false until then → avatar placeholder).
 */
/**
 * Display name for a peer that has media but no Presence row — a gateway peer
 * or the mixer composite. peerIds are `h323_<ip>_<port>`, `sip_<...>`, `mixer`.
 * Presence peers never reach here; they carry their real name.
 */
function friendlyPeerName(pid: string): string {
  if (pid === 'mixer') return 'Mixed view'
  if (pid.startsWith('h323_')) return 'H.323 endpoint'
  if (pid.startsWith('sip_')) return 'SIP endpoint'
  return pid
}

/**
 * Peers that are SFU-only by design and never appear in Presence.
 *
 * A gateway endpoint and the mixer composite join the media plane without ever
 * joining the Phoenix channel, so they have to be admitted to the roster on the
 * strength of their stream alone. Everyone else — every browser — has a
 * Presence row for as long as they are here, so a stream with no row behind it
 * is not a participant we are missing. It is one who has left.
 */
export function isMediaOnlyPeer(pid: string): boolean {
  return pid === 'mixer' || pid.startsWith('h323_') || pid.startsWith('sip_')
}

export type RemotePeer = {
  peerId: string
  name: string
  stream: MediaStream | null
  videoOn: boolean
  muted: boolean
  isSpeaking: boolean
  /** Separate stream for this peer's screen share (sourceType 'screen'), or null. */
  screenStream: MediaStream | null
  /** True while this peer is sharing their screen. */
  isSharingScreen: boolean
  /** Hand up. From Presence, so it survives a late join. */
  handRaised: boolean
  /** Extra cameras this peer has published, each rendered as its own tile. */
  aux: AuxSource[]
  /**
   * sourceType of this peer's primary video consumer — 'camera', or 'h323' /
   * 'sip' for a gateway endpoint. The tile needs it to name its own stream;
   * see RemotePeer in useConsumers for what assuming 'camera' cost.
   */
  videoSourceType?: string
  /**
   * Mic and camera as the peer itself reports them, or null/absent when it
   * does not (gateways, older clients). The roster shows this; `muted` and
   * `videoOn` above are what this viewer is receiving, which the tile needs and
   * the roster must not use. See PeerPresence.media.
   */
  reported?: { micOn: boolean; camOn: boolean } | null
  /**
   * In the meeting as a screen only (`isShareOnlyPeer`): its screen goes on
   * the stage like anyone's, but it gets no tile — see services/share-only-roster.
   */
  shareOnly?: boolean
}

export type MeetingConnectionState = {
  /** Phoenix WebSocket is open */
  connected: boolean
  /** Mediasoup transports + device are ready */
  sfuReady: boolean
  /** Local camera/mic stream */
  localStream: MediaStream | null
  /** Remote peers keyed by peerId */
  remotePeers: Map<string, RemotePeer>
  /** This client's SFU peer ID */
  peerId: string | null
  /** Dominant speaker's peerId (sticky, debounced) — drives spotlight/sidebar. */
  activeSpeakerId: string | null
  /** The active speaker while audible; null after 1.5 s of silence. Drives the speaking ring. */
  speakingId: string | null
  /**
   * Subscribe to a raw room-channel event. Exposed so feature hooks (lobby,
   * lock) can listen for server broadcasts without re-opening the socket.
   */
  subscribe: (event: string, handler: (payload: Record<string, unknown>) => void) => () => void
  /** Send a raw room-channel event (moderator actions, role queries). */
  sendEvent: (event: string, payload: Record<string, unknown>) => void
  /** The same, but waiting for the server to say whether it did it. */
  request: (
    event: string,
    payload: Record<string, unknown>,
  ) => Promise<{ status: string; response: Record<string, unknown> }>
  /** True once the room channel is joined — events sent earlier are dropped. */
  channelJoined: boolean
  /** The room ID derived from channelTopic */
  roomId: string | null
  /** Error from SFU join, if any */
  error: string | null
  /** Publish a local screen-share stream (video + optional audio) to the SFU. */
  publishScreenShare: (stream: MediaStream) => Promise<void>
  /** Stop & tear down the local screen-share producers. */
  unpublishScreenShare: () => void
  /** A local share is running that the room is not receiving yet. */
  screenSharePending: () => boolean
  /** Extra cameras this client is currently publishing. */
  localVideoInputs: LocalVideoInput[]
  /** Capture `deviceId` and publish it as an extra camera. Video only. */
  publishVideoInput: (deviceId: string, label: string) => Promise<void>
  /** Stop & tear down one extra camera by its producer slot. */
  unpublishVideoInput: (sourceId: string) => void
  /** Stop & tear down every extra camera (used on moderator force-stop-video). */
  unpublishAllVideoInputs: () => void
}

/**
 * Drop the cameras this viewer chose to hide (participant menu → Mute video)
 * from the set lastN keeps flowing — last, so a pin or speaking cannot put them
 * back. Null stays null: that is the cap disengaged, where everything flows.
 */
function withoutHiddenCameras(
  keys: Set<string> | null,
  hidden: Record<string, true>,
): Set<string> | null {
  if (!keys) return keys
  const ids = Object.keys(hidden)
  if (ids.length === 0) return keys
  for (const id of ids) keys.delete(layerPreference.layerKey(id, 'camera'))
  return keys
}

export function useMeetingConnection(): MeetingConnectionState {
  const credentials = useMeetingSessionStore((s) => s.roomCredentials)
  const micOn = useMeetingSessionStore((s) => s.micOn)
  const camOn = useMeetingSessionStore((s) => s.camOn)
  const areIncomingVideosPaused = useMeetingSessionStore((s) => s.areIncomingVideosPaused)
  const areIncomingAudiosPaused = useMeetingSessionStore((s) => s.areIncomingAudiosPaused)
  const authUser = useAuthStore((s) => s.user)

  // Derive room ID from channel topic ("room:<id>" → "<id>")
  const roomId = credentials?.channelTopic
    ? credentials.channelTopic.replace(/^room:/, '')
    : null

  // The two ids that have to agree for an active-speaker broadcast to land:
  // the Phoenix topic we are subscribed to, and the room id the SFU knows us
  // by (which is what it puts in its POST to signaling). Printed together so a
  // mismatch is a comparison rather than an investigation.
  useEffect(() => {
    if (!roomId) return
    console.info('[activeSpeaker] channel identity', {
      channelTopic: credentials?.channelTopic,
      sfuRoomId: roomId,
      // Which signaling server this socket is actually on. The SFU posts its
      // active-speaker list to ITS configured signaling host, so if these are
      // two different servers the broadcast is delivered correctly to a room
      // nobody in this browser is listening to — which is exactly how this
      // looked like a broken feature rather than a split configuration.
      signalingWsUrl: credentials?.signalingWsUrl,
    })
  }, [roomId, credentials?.channelTopic])

  // Peer identity MUST match the server's: the signaling server keys Presence
  // (roster) and every media:* broadcast by the room-JWT `sub` (user_socket.ex:
  // user_id = claims["sub"]), and under per-org identity that `sub` can diverge
  // from the global authUser.id. So derive peerId from the room token's `sub`
  // whenever we have a token, and only fall back to authUser.id when we don't.
  // This keeps the self-filter, Presence roster, SFU producer, and consumer keys
  // all on the same identity — otherwise an authenticated user whose id ≠ sub
  // sees themselves as a peer tile and their media never attaches to their row.
  const peerId = credentials ? decodePeerIdFromToken(credentials) : (authUser?.id ?? null)

  // Ref forwarded to useSfuJoin so the send-transport 'produce' handler can
  // call sendEvent without creating a stale closure
  const sendEventRef = useRef<((event: string, payload: Record<string, unknown>) => void) | null>(null)

  // Phase 1 — Phoenix channel
  const { connected, channelJoined, sendEvent, request, subscribe } = usePhoenixChannel(credentials)

  // Room tokens last an hour; the meeting may not. Renewed in the background
  // for as long as these credentials are in use — see services/room-token.
  useEffect(() => {
    const roomId = credentials?.channelTopic?.replace(/^room:/, '')
    if (!roomId || !credentials?.roomToken) return
    return scheduleRoomTokenRenewal(roomId, credentials.roomToken)
  }, [credentials?.channelTopic, credentials?.roomToken])

  // Keep ref up-to-date so useSfuJoin can call sendEvent from the produce event
  sendEventRef.current = channelJoined ? sendEvent : null

  // Tell the room our mic and camera, on every join and every change.
  //
  // The server keeps them in Presence (`audio_muted`, `video_on`), which is
  // what other participants' rosters show — and what someone joining later is
  // replayed. Nothing sent these, so every roster read our state off whatever
  // media it happened to be receiving from us, and showed us muted with the
  // camera off whenever that was nothing. See services/media-report.
  const reportedRef = useRef<{ mic: boolean; cam: boolean } | null>(null)
  useEffect(() => {
    if (!channelJoined) {
      reportedRef.current = null
      return
    }
    const send = sendEventRef.current
    if (!send) return
    const now = { mic: micOn, cam: camOn }
    for (const event of mediaReports(reportedRef.current, now)) send(event, {})
    reportedRef.current = now
  }, [channelJoined, micOn, camOn])

  // Roster — authoritative list of peers + names from Phoenix Presence, scoped
  // to the room channel so a breakout switch never shows the previous room.
  const presence = usePresence(subscribe, credentials?.channelTopic ?? null)

  // Who is talking — broadcast by the SFU's AudioLevelObserver. Independent of
  // media state, so it is safe to read before/without any consumer.
  // `speakers` is the ranked list, most recently dominant first. It drove only
  // the highlighted tile until the audio cap needed to know who to keep audible.
  const { activeSpeakerId, speakers, speakingId } = useActiveSpeakers(subscribe)

  // Phase 2 — SFU join, once the Phoenix channel has joined. A channel drop no
  // longer ends it: the session is held for MEDIA_HOLD_MS, so a few seconds of
  // lost network costs no teardown, no `/leave` and no rebuild. See
  // services/media-session-hold.
  const mediaSessionOn = useMediaSessionHold(channelJoined, roomId && peerId ? `${roomId}|${peerId}` : null)
  const sfuState = useSfuJoin(
    mediaSessionOn ? roomId : null,
    mediaSessionOn ? peerId : null,
    sendEventRef,
  )

  // Phase 3 — local media producers
  const selectedBackground = useMeetingPreferencesStore((s) => s.meetingBackground)
  const selectedMicId = useDevicesStore((s) => s.selectedMicId)
  const selectedCamId = useDevicesStore((s) => s.selectedCamId)
  const audioProcessing = useDevicesStore((s) => s.audioProcessing)
  const noiseCancellation = useDevicesStore((s) => s.noiseCancellation)
  // From the selected audio preset. Both are stable object references in the
  // store — replaced only when a preset or a switch changes — so they can be
  // effect dependencies without restarting capture every render.
  const audioFormat = useDevicesStore((s) => s.audioFormat)
  const opus = useDevicesStore((s) => s.opus)
  const mediaRetryToken = useDevicesStore((s) => s.mediaRetryToken)
  // While the room is listening to a shared video, publish the plain microphone
  // with the browser's own filters instead of RNNoise.
  //
  // Nothing carries that video's sound between participants — every client
  // plays it locally — so anything of it that reaches the room does so by
  // leaking out of someone's speakers and back into their microphone. Two
  // things then make that leak much worse than it needs to be: RNNoise is
  // trained on speech, so music through it comes back warbled rather than
  // quiet, and turning it on switches the browser's own noise suppression off
  // (see `micConstraints`). Handing the browser its own microphone back for the
  // duration lets the echo canceller work on the signal it was built for.
  //
  // The user's saved preference is untouched — only what is published changes,
  // and it changes back when playback stops.
  const mediaAudioPlaying = useMeetingSessionStore((s) => s.mediaAudioPlaying)
  const effectiveNoiseCancellation = noiseCancellation && !mediaAudioPlaying
  const effectiveAudioProcessing = useMemo(
    () =>
      mediaAudioPlaying
        ? { ...audioProcessing, echoCancellation: true, noiseSuppression: true }
        : audioProcessing,
    [audioProcessing, mediaAudioPlaying],
  )
  const videoQualityName = useDevicesStore((s) => s.videoQuality)
  const webcamFrameRate = useDevicesStore((s) => s.webcamFrameRate)
  const aspectRatio = useDevicesStore((s) => s.aspectRatio)
  // The preset, with the two overrides folded in. Memoised because it is a
  // dependency of the capture effects — a fresh object each render would
  // re-apply constraints continuously.
  const videoQuality = useMemo(() => {
    const preset = VIDEO_QUALITY_PRESETS[videoQualityName]
    return {
      ...preset,
      frameRate: webcamFrameRate ?? preset.frameRate,
      // Height follows the ratio so the two controls describe one capture
      // rather than contradicting each other.
      height: Math.round(preset.width / aspectRatio),
    }
  }, [videoQualityName, webcamFrameRate, aspectRatio])
  const publishBackground = useMemo(
    () =>
      selectedBackground
        ? { url: selectedBackground.url || undefined, blurPx: selectedBackground.blurPx }
        : null,
    [selectedBackground],
  )

  const { localStream } = useLocalMedia({
    sendTransport: sfuState.sendTransport,
    device: sfuState.device,
    roomId: sfuState.ready ? roomId : null,
    peerId: sfuState.ready ? peerId : null,
    control: sfuState.control,
    // The owning node, for the teardown HTTP fallback — camera-off closes the
    // video producer now, and a close the server never hears about is replayed
    // to the next joiner.
    sfuNodeUrl: sfuState.sfuNodeUrl,
    simulcastEnabled: credentials?.simulcastEnabled ?? true,
    micOn,
    camOn,
    // Composited into the published track, not just the local tile. Memoised:
    // this object is a dependency of the swap effect, and a fresh literal every
    // render would restart the pipeline continuously.
    background: publishBackground,
    // The chosen microphone, from the lobby or the control bar. Capture used to
    // ignore it, so the meeting always opened the system default.
    micDeviceId: selectedMicId,
    camDeviceId: selectedCamId,
    audioProcessing: effectiveAudioProcessing,
    videoQuality,
    noiseCancellation: effectiveNoiseCancellation,
    // Capture format and Opus settings from the selected audio preset. The
    // format re-shapes the running track; the Opus options are read once, when
    // the producer is created.
    audioFormat,
    opus,
    // Rebuild the producers when a device the browser refused at join is later
    // granted — see `mediaRetryToken` in the devices store.
    mediaRetryToken,
  })

  // Phase 4 — remote consumers (media only). Keyed by peerId, supplies the
  // MediaStream + live audio/video flags for whoever currently has producers.
  // lastN — which peers' cameras are worth the bandwidth.
  //
  // Ordered by who is most likely to be looked at: pinned first, then the
  // active speaker, then recent speakers, then everyone else. The person
  // talking must never be the one we paused, which is why roster order alone
  // will not do.
  //
  // null when the cap is 'auto': no cap, consume everything, which is the
  // behaviour anyone who has not touched the setting already has.
  // Peers that publish media but never appear in Presence — a gateway-joined
  // H.323 / SIP endpoint, and the mixer composite. Fed from the consumers we
  // actually hold (see the effect below useConsumers), not from the
  // media:new_producer broadcast: that fires once, when the producer appears,
  // so a browser that joins after the endpoint never sees it and the peer would
  // stay outside the lastN set — paused, frozen — for the whole call.

  const pinnedIds = useMeetingLayoutStore((s) => s.pinnedIds)
  /**
   * Which streams stay live — read from what is actually painted.
   *
   * This replaced a priority-ordered slice of the roster, which could not have
   * worked: VideoGrid pages the FULL roster with its own page size and its own
   * touch-device cap, so a set derived out here would have paused people who
   * were on screen. Tiles declare themselves instead; see rendered-tiles.
   *
   * Deliberately does NOT consult tileDensity or selectMaxTiles. VideoGrid's
   * own slice IS the cap, and a second copy of that arithmetic is the thing
   * that would drift.
   */
  const renderedKeys = useSyncExternalStore(
    renderedTiles.subscribe,
    renderedTiles.getSnapshot,
    renderedTiles.getSnapshot,
  )
  /**
   * The stream made fullscreen, if any. A fullscreen tile covers the grid but
   * leaves it mounted, so without this every tile on the page kept streaming
   * behind it. See fullscreen-focus.
   */
  const fullscreenKey = useSyncExternalStore(
    fullscreenFocus.subscribe,
    fullscreenFocus.getSnapshot,
    fullscreenFocus.getServerSnapshot,
  )
  useEffect(() => {
    vclog('consumer', fullscreenKey !== null
      ? 'fullscreen — every other camera paused'
      : 'not fullscreen — on-screen cameras flow', { key: fullscreenKey })
  }, [fullscreenKey])
  const hiddenVideoIds = useParticipantVideoHiddenStore((s) => s.hidden)
  const visibleSourceKeys = useMemo(() => withoutHiddenCameras(fullscreenFocus.visibleCameraKeys({
    fullscreenKey,
    renderedKeys,   // null: nothing painted yet, or drift detected
    // Never pausable outside fullscreen, whatever is on screen: someone
    // speaking must not be the one we stopped, and a pinned peer was pinned on
    // purpose. Built with the same helper the consumer side uses, so the
    // spellings cannot diverge.
    pinnedKeys: pinnedIds.map((id) => layerPreference.layerKey(id, 'camera')),
    activeSpeakerKey: activeSpeakerId ? layerPreference.layerKey(activeSpeakerId, 'camera') : null,
  }), hiddenVideoIds), [fullscreenKey, renderedKeys, pinnedIds, activeSpeakerId, hiddenVideoIds])

  const { remotePeers: mediaPeers, screenStreams, auxSources } = useConsumers({
    recvTransport: sfuState.recvTransport,
    peerId: sfuState.ready ? peerId : null,
    control: sfuState.control,
    subscribe,
    ready: sfuState.ready,
    pauseIncomingVideo: areIncomingVideosPaused,
    pauseIncomingAudio: areIncomingAudiosPaused,
    visibleSourceKeys,
    rankedSpeakers: speakers,
    // useConsumers sends the initial readyToConsume itself, once subscribed.
    rtpCapabilities: sfuState.device?.rtpCapabilities,
  })

  // The gateway special case used to live here: H.323 / SIP / mixer peers have
  // no Presence row, so they had to be discovered from the consumer map and
  // folded into the visible set by hand, or their video froze. That whole
  // problem was an artefact of deriving visibility from the roster. A gateway
  // peer that is rendered as a tile now retains its key like anyone else.

  // Merge: Presence drives the roster (who + name); consumers attach media.
  // A peer in Presence with no consumer yet renders as an avatar placeholder
  // (stream null, videoOn false) until its stream arrives. Excludes self.
  const remotePeers = useMemo<Map<string, RemotePeer>>(() => {
    const merged = new Map<string, RemotePeer>()
    for (const [pid, info] of presence) {
      if (pid === peerId) continue
      const media = mediaPeers.get(pid)
      const screenStream = screenStreams.get(pid) ?? null
      merged.set(pid, {
        peerId: pid,
        name: info.name || pid,
        stream: media?.stream ?? null,
        videoOn: media?.videoOn ?? false,
        muted: media?.muted ?? true,
        isSpeaking: media?.isSpeaking ?? false,
        screenStream,
        isSharingScreen: !!screenStream,
        handRaised: info.handRaised,
        aux: auxSources.get(pid) ?? [],
        videoSourceType: media?.videoSourceType,
        reported: info.media,
        shareOnly: info.shareOnly,
      })
    }
    // Media-only peers: anyone whose stream we consumed but who has no Presence
    // row. A gateway-joined H.323 / SIP endpoint (and the mixer composite) is an
    // SFU peer, never a Phoenix presence member, so without this its audio and
    // video arrive and decode with no tile to show them — the "media but no
    // roster row" case the log below names. Rendering by stream, not roster, is
    // also what makes this work across SFU nodes: the endpoint's producer is
    // piped to every node and each announces it locally, so every browser sees
    // it regardless of which node carries the gateway.
    for (const [pid, media] of mediaPeers) {
      if (pid === peerId || merged.has(pid)) continue
      // Gateways and the mixer only. This used to admit ANY peer we had a
      // consumer for, which quietly turned every leaked consumer into a
      // permanent roster row: a participant leaves, Presence correctly drops
      // them, and the leftover stream re-adds them here with nothing left to
      // take them away again. Rejoining mints a fresh id, so each round trip
      // left another phantom behind and the participant count climbed into the
      // dozens with three people in the room. Their name gave it away too —
      // `friendlyPeerName` has nothing to call them, so it showed the raw id.
      if (!isMediaOnlyPeer(pid)) continue
      const screenStream = screenStreams.get(pid) ?? null
      merged.set(pid, {
        peerId: pid,
        name: friendlyPeerName(pid),
        stream: media.stream ?? null,
        videoOn: media.videoOn ?? false,
        muted: media.muted ?? true,
        isSpeaking: media.isSpeaking ?? false,
        screenStream,
        isSharingScreen: !!screenStream,
        handRaised: false,
        aux: auxSources.get(pid) ?? [],
        // The branch that matters for this field: every peer reaching it is a
        // gateway or the mixer, i.e. exactly the peers whose sourceType is not
        // 'camera'.
        videoSourceType: media.videoSourceType,
      })
    }

    // Roster is Presence-driven; media attaches by matching peerId. Log the join
    // between the two so a "presence entry but no stream" (missing consumer) or a
    // "media but no roster row" (peerId mismatch) is obvious at a glance.
    // Guarded, because the argument is built BEFORE vclog can decline it.
    // `vclog` no-ops when debug is off, but its payload is an object literal
    // evaluated at the call site — so this mapped the entire roster into ~290
    // fresh objects and filtered ~302 keys on every join, leave, mute, camera
    // toggle and producer event, in the deployed build, for a line nobody would
    // ever read. The same trap was already fixed once in useConsumers; this is
    // the other call site that carries a whole-roster payload.
    if (vcDebugEnabled) {
      vclog('tile', 'roster merged (presence + media)', {
        self: peerId,
        rows: [...merged.values()].map((p) => ({ peerId: p.peerId, name: p.name, hasStream: !!p.stream, videoOn: p.videoOn })),
        mediaOnlyPeerIds: [...mediaPeers.keys()].filter((k) => !presence.has(k)),
      })
    }
    return merged
  }, [presence, mediaPeers, screenStreams, auxSources, peerId])

  // ─── Screen-share publishing ────────────────────────────────────
  // Produce the local screen track(s) to the SFU with appData.sourceType
  // 'screen'. The send-transport 'produce' handler (useSfuJoin) auto-broadcasts
  // `media:new_producer` with that sourceType, so remote peers consume it as a
  // distinct stream. We additionally fire `media:screenshare_started/stopped`.
  //
  // The publisher owns when: it publishes whenever there is both a share and a
  // send transport, again after a session rebuild, and closes a producer that
  // finishes for a share already stopped. See services/screen-share-publisher.
  // How each track is produced lives in services/screen-producer, shared with
  // the Screen Share page.

  /**
   * Tell the SFU a producer is gone, on a channel that answers.
   *
   * `producer.close()` is local — the server keeps the producer unless it is
   * told, and hands it to the next person who joins, because `readyToConsume`
   * replays whatever the room holds. That is how a stopped screen share came
   * back for anyone who refreshed: everybody already in the meeting had been
   * told over signalling, so only a rejoin ever saw it.
   *
   * The control socket is preferred over the node URL for the reasons in
   * `producer-teardown`; a failure on both is LOGGED rather than dropped, which
   * is the difference between an orphaned producer nobody hears about and one
   * that shows up in a session log.
   */
  const closeServerProducer = useCallback((producerId: string, nodeUrl: string | null) => {
    void closeRemoteProducer(producerId, {
      control: sfuState.control,
      http: nodeUrl && roomId
        ? (id) => fetch(`${nodeUrl}/rooms/${roomId}/producers/${id}`, {
            method: 'DELETE',
            headers: { 'x-api-key': config.sfu.apiKey },
          })
        : null,
    }).then((outcome) => {
      if (outcome.via === 'none') {
        vclog('producer', 'server-side producer NOT freed — it will be replayed to the next joiner', {
          producerId,
          reason: outcome.reason,
        })
      }
    })
  }, [sfuState.control, roomId])

  // Read by the publisher's callbacks, which outlive the render that made them.
  const closeServerProducerRef = useRef(closeServerProducer)
  closeServerProducerRef.current = closeServerProducer
  const sfuNodeUrlRef = useRef(sfuState.sfuNodeUrl)
  sfuNodeUrlRef.current = sfuState.sfuNodeUrl

  const screenPublisherRef = useRef<ScreenSharePublisher<Producer, Transport> | null>(null)
  if (!screenPublisherRef.current) {
    screenPublisherRef.current = new ScreenSharePublisher<Producer, Transport>({
      produce: (transport, track) => produceScreenTrack(transport, track),
      onProduced: (producer) => {
        // Registered so the share's own stats panel has something to read.
        vclog('producer', 'screen stats registered', { key: registerProducerStats(producer as never) })
      },
      onClosed: (producer, serverAlive) => {
        unregisterProducerStats(producer as never)
        try { producer.close() } catch { /* already closed */ }
        if (!serverAlive) return
        // Free the server-side producer and tell remotes to drop the consumer.
        closeServerProducerRef.current(producer.id, sfuNodeUrlRef.current)
        sendEventRef.current?.('media:producer_closed', { producerId: producer.id })
      },
      announceStarted: () => sendEventRef.current?.('media:screenshare_started', {}),
      // The producer ids let a viewer close exactly this share, never a newer
      // one that reached it first over the SFU socket (useConsumers).
      announceStopped: (producerIds) => sendEventRef.current?.('media:screenshare_stopped', { producerIds }),
      log: (message, data) => vclog('producer', message, data),
    })
  }

  // Every send transport the session has, in order: none yet, the first, and a
  // new one after each rebuild. The publisher republishes a running share.
  useEffect(() => {
    screenPublisherRef.current?.setTransport(sfuState.sendTransport ?? null)
  }, [sfuState.sendTransport])

  const publishScreenShare = useCallback(
    (stream: MediaStream) => screenPublisherRef.current!.start(stream),
    [],
  )
  const unpublishScreenShare = useCallback(() => screenPublisherRef.current?.stop(), [])
  /** A share is running that nobody is receiving yet. */
  const screenSharePending = useCallback(() => screenPublisherRef.current?.pending ?? false, [])
  // A shared tab's audio follows the microphone, however it was muted — the
  // button, the shortcut, a moderator's mute, mute on entry. See setAudioMuted.
  useEffect(() => {
    screenPublisherRef.current?.setAudioMuted(!micOn)
  }, [micOn])

  // ─── Additional camera inputs ───────────────────────────────────
  // Same shape as screen share: a second (or third) video producer on the same
  // send transport, tagged with its own `sourceType` slot so the SFU fan-out and
  // the receiver's `${peerId}:${kind}:${sourceType}` dedup key stay unique. The
  // device name rides along in `appData.label` and the SFU replays it to every
  // peer — including late joiners — on the `newConsumer` push.
  const [localVideoInputs, setLocalVideoInputs] = useState<LocalVideoInput[]>([])
  const auxProducersRef = useRef<Map<string, Producer>>(new Map())
  const auxStreamsRef = useRef<Map<string, MediaStream>>(new Map())
  /**
   * The inputs the user has ADDED, captured or not.
   *
   * Separate from the producers and streams because an added input outlives
   * both. Camera-off stops its capture and closes its producer, a rebuilt
   * session closes every producer with the old transport — and in each case
   * the input is still the user's, and comes back when the camera does.
   */
  const configuredInputsRef = useRef<Map<string, { deviceId: string; label: string }>>(new Map())
  /** Slots with a capture or produce in flight, so a re-run cannot open twice. */
  const openingInputsRef = useRef<Set<string>>(new Set())
  // Read inside async work that outlives the render that started it: a camera
  // turned off while getUserMedia is still resolving must not end up live.
  const camOnRef = useRef(camOn)
  camOnRef.current = camOn

  /**
   * Close every extra camera when this hook goes.
   *
   * These are opened with a direct `getUserMedia` rather than through
   * `deviceManager`, so nothing else knows they exist and no refcount covers
   * them. They were stopped only by an explicit unpublish — so a second camera
   * that was still running when the user left the meeting stayed open for the
   * life of the tab, holding the browser's camera-in-use indicator on with no
   * control anywhere to turn it off.
   *
   * Reads the ref at cleanup rather than closing over its contents, because the
   * map is mutated throughout the call and the set to stop is whatever is open
   * at the moment of leaving.
   */
  useEffect(() => {
    const streams = auxStreamsRef.current
    return () => {
      streams.forEach((stream) => stream.getTracks().forEach((t) => t.stop()))
      streams.clear()
    }
  }, [])

  /** Insert or replace one input's row, kept in slot order for a stable grid. */
  const upsertVideoInput = useCallback((input: LocalVideoInput) => {
    setLocalVideoInputs((prev) =>
      [...prev.filter((i) => i.sourceId !== input.sourceId), input]
        .sort((a, b) => a.sourceId.localeCompare(b.sourceId)),
    )
  }, [])

  /** Free the local capture + server producer for one slot. Safe to call twice. */
  const teardownVideoInput = useCallback((sourceId: string, nodeUrl: string | null) => {
    const producer = auxProducersRef.current.get(sourceId)
    if (producer) {
      const producerId = producer.id
      unregisterProducerStats(producer as never)
      try { producer.close() } catch { /* already closed */ }
      auxProducersRef.current.delete(sourceId)
      // Same channel as the screen share, and for the same reason: an extra
      // camera the server keeps is replayed to whoever joins next.
      closeServerProducer(producerId, nodeUrl)
      sendEventRef.current?.('media:producer_closed', { producerId })
    }
    // `media:producer_closed` alone is not enough to clear the remote tile.
    // It matches on producerId, and the id a peer consumed is its own node's
    // (translated) id for a cross-node mirror — so the match silently misses
    // and the last decoded frame stays frozen on their grid. Screen share hit
    // exactly this and solved it with a peer+source keyed event; do the same.
    // Sent unconditionally, so a slot with no live producer still gets cleared.
    sendEventRef.current?.('media:video_input_stopped', { sourceId })
    const stream = auxStreamsRef.current.get(sourceId)
    stream?.getTracks().forEach((t) => t.stop())
    auxStreamsRef.current.delete(sourceId)
  }, [closeServerProducer])

  /** Remove an input altogether: the user closed it, or its device left. */
  const unpublishVideoInput = useCallback((sourceId: string) => {
    configuredInputsRef.current.delete(sourceId)
    teardownVideoInput(sourceId, sfuState.sfuNodeUrl)
    setLocalVideoInputs((prev) => prev.filter((i) => i.sourceId !== sourceId))
    vclog('producer', 'additional video input removed', { sourceId })
  }, [teardownVideoInput, sfuState.sfuNodeUrl])

  // The `ended` listener and the devicechange check are bound once per capture
  // and once per mount, long before the render that will be current when they
  // fire. Calling through a ref is what keeps them from tearing down against a
  // node URL from a session that has since been rebuilt.
  const unpublishVideoInputRef = useRef(unpublishVideoInput)
  unpublishVideoInputRef.current = unpublishVideoInput

  const unpublishAllVideoInputs = useCallback(() => {
    const slots = new Set([...configuredInputsRef.current.keys(), ...auxProducersRef.current.keys()])
    configuredInputsRef.current.clear()
    for (const sourceId of slots) teardownVideoInput(sourceId, sfuState.sfuNodeUrl)
    setLocalVideoInputs((prev) => (prev.length === 0 ? prev : []))
  }, [teardownVideoInput, sfuState.sfuNodeUrl])

  /**
   * Stop sending one input but keep it added — the camera control went off.
   *
   * The capture is STOPPED, not disabled: a disabled track keeps the device
   * open and its light on, which is the "camera off but still watching" the
   * user turned it off to avoid.
   */
  const suspendVideoInput = useCallback((sourceId: string) => {
    const cfg = configuredInputsRef.current.get(sourceId)
    teardownVideoInput(sourceId, sfuState.sfuNodeUrl)
    if (cfg) upsertVideoInput({ sourceId, deviceId: cfg.deviceId, label: cfg.label, stream: null, active: false })
    vclog('producer', 'additional video input suspended (camera off)', { sourceId })
  }, [teardownVideoInput, upsertVideoInput, sfuState.sfuNodeUrl])

  /**
   * Capture (or re-use the capture of) one added input and publish it on the
   * CURRENT send transport.
   *
   * Re-use matters after a session rebuild: the producer died with the old
   * transport, but the camera itself is fine. It used to die too — `produce()`
   * defaults to `stopTracks: true`, so closing the transport STOPPED the track,
   * the tile went black, and because a stopped track never fires `ended`,
   * unplugging the camera afterwards could no longer remove it either.
   */
  const startVideoInput = useCallback(async (sourceId: string) => {
    const transport = sfuState.sendTransport
    const cfg = configuredInputsRef.current.get(sourceId)
    if (!transport || transport.closed || !cfg) return
    if (openingInputsRef.current.has(sourceId)) return
    const existing = auxProducersRef.current.get(sourceId)
    if (existing && !existing.closed) return

    openingInputsRef.current.add(sourceId)
    // Set when the session is rebuilt under this call. The reconcile run for
    // the new transport found this slot busy and skipped it, so this call is
    // the one that has to hand over once it is out of the way.
    let handOver = false
    try {
      // A producer the old transport closed: drop it, so the stats key and the
      // slot are free for the one about to be made.
      if (existing) {
        unregisterProducerStats(existing as never)
        auxProducersRef.current.delete(sourceId)
      }

      let stream = auxStreamsRef.current.get(sourceId) ?? null
      let track = stream?.getVideoTracks()[0]
      if (!stream || !track || track.readyState !== 'live') {
        stream?.getTracks().forEach((t) => t.stop())
        auxStreamsRef.current.delete(sourceId)
        // Video only — a second mic would echo and confuse active-speaker attribution.
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: cfg.deviceId },
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 30, max: 30 },
          },
          audio: false,
        })
        track = stream.getVideoTracks()[0]
        if (!track) {
          stream.getTracks().forEach((t) => t.stop())
          throw new Error('no video track for the selected device')
        }
        // Unplugging the camera ends the track; drop the input rather than
        // leaving a frozen last frame on every peer's grid.
        track.addEventListener('ended', () => unpublishVideoInputRef.current(sourceId))
        // Reserve the slot before the async produce() so two fast clicks can't
        // both resolve onto `aux1`.
        auxStreamsRef.current.set(sourceId, stream)
      }

      // The ground can move while the capture resolves: the input removed, the
      // camera turned off, the session rebuilt. Each means this capture is not
      // wanted on this transport, and an open device nobody shows is exactly
      // the stray camera light the cleanup above exists to prevent.
      // A closed transport alone is different: the input is still wanted, so the
      // capture is kept for the next transport to re-use.
      const wanted = configuredInputsRef.current.has(sourceId) && camOnRef.current
      if (!wanted) {
        stream.getTracks().forEach((t) => t.stop())
        auxStreamsRef.current.delete(sourceId)
        return
      }
      if (transport.closed) { handOver = true; return }

      let producer: Producer
      try {
        // Single encoding, like screen share: an extra camera is secondary content
        // and simulcast for it would triple the encode cost of the whole call.
        // `networkPriority: 'medium'` for the same reason as screen share: it is
        // secondary content and should give up bitrate first, not compete with
        // the primary camera/mic, when the shared transport renegotiates.
        producer = await transport.produce({
          track,
          encodings: [{ maxBitrate: 900_000, networkPriority: 'medium' }],
          appData: { sourceType: sourceId, label: cfg.label },
          // We own this capture, not the producer — see the note above.
          stopTracks: false,
        })
      } catch (err) {
        // The session was rebuilt under us. Not a failure of this input: keep
        // the capture and let the next transport publish it.
        if (transport.closed) {
          vclog('producer', 'additional video input: transport closed during produce — will retry on the next one', { sourceId })
          handOver = true
          return
        }
        auxStreamsRef.current.delete(sourceId)
        stream.getTracks().forEach((t) => t.stop())
        vclog('producer', 'additional video input FAILED', { sourceId, label: cfg.label, error: (err as Error).message })
        throw err
      }
      auxProducersRef.current.set(sourceId, producer)
      // Same registration the primary camera gets. The key comes back out of
      // appData rather than being rebuilt from `sourceId` here, because the slot
      // is a runtime value and a second copy of it is how these keys drift.
      vclog('producer', 'aux stats registered', { key: registerProducerStats(producer as never) })

      // Turned off, or removed, while produce() was in flight.
      if (!configuredInputsRef.current.has(sourceId)) {
        teardownVideoInput(sourceId, sfuState.sfuNodeUrl)
        return
      }
      if (!camOnRef.current) {
        suspendVideoInput(sourceId)
        return
      }

      upsertVideoInput({ sourceId, deviceId: cfg.deviceId, label: cfg.label, stream, active: true })
      vclog('producer', 'additional video input published', { sourceId, label: cfg.label, producerId: producer.id })
    } finally {
      openingInputsRef.current.delete(sourceId)
      // Through the ref: the latest render's start, bound to the latest
      // transport. If that one is not up yet it returns, and the reconcile
      // effect starts the input when it arrives.
      if (handOver) {
        setTimeout(() => {
          startVideoInputRef.current?.(sourceId).catch(() => unpublishVideoInputRef.current(sourceId))
        }, 0)
      }
    }
  }, [sfuState.sendTransport, sfuState.sfuNodeUrl, teardownVideoInput, suspendVideoInput, upsertVideoInput])
  const startVideoInputRef = useRef<typeof startVideoInput | null>(null)
  startVideoInputRef.current = startVideoInput

  const publishVideoInput = useCallback(async (deviceId: string, label: string) => {
    if (configuredInputsRef.current.size >= MAX_ADDITIONAL_VIDEO_INPUTS) return

    // First free slot, so removing aux1 and adding again reuses it rather than
    // growing the ordinal forever.
    let sourceId = ''
    for (let i = 1; i <= MAX_ADDITIONAL_VIDEO_INPUTS; i++) {
      if (!configuredInputsRef.current.has(`aux${i}`)) { sourceId = `aux${i}`; break }
    }
    if (!sourceId) return

    // A view-only attendee publishes nothing, extra cameras included. The
    // control that reaches this is already hidden for them; the guard is here
    // because this function opens a device, and the UI is not the place to keep
    // that promise.
    if (useMeetingSessionStore.getState().viewOnly) return

    configuredInputsRef.current.set(sourceId, { deviceId, label })

    // Adding a camera is not turning the camera on. With the camera control off
    // the input is added and shown as off, and nothing is captured or sent
    // until the user turns the camera on — the reconcile effect below starts it.
    // It used to open and publish straight away, switching on video for
    // someone who had joined with it off.
    if (!camOnRef.current || !sfuState.sendTransport) {
      upsertVideoInput({ sourceId, deviceId, label, stream: null, active: false })
      vclog('producer', 'additional video input added, not started', {
        sourceId, label, reason: camOnRef.current ? 'no send transport yet' : 'camera is off',
      })
      return
    }

    try {
      await startVideoInput(sourceId)
    } catch (err) {
      // Never became an input; the dialog shows the error.
      configuredInputsRef.current.delete(sourceId)
      setLocalVideoInputs((prev) => prev.filter((i) => i.sourceId !== sourceId))
      throw err
    }
  }, [sfuState.sendTransport, startVideoInput, upsertVideoInput])

  /**
   * Keep every added input in step with the camera control and the session.
   *
   *  - Camera off: stop each input's capture and producer. The camera control
   *    used to reach only the primary camera, so a second camera went on
   *    transmitting after the user had turned their video off.
   *  - Camera on: start any input that is not publishing.
   *  - A rebuilt session (new send transport): re-publish each input on it.
   *    Nothing re-made these before, so after a network drop the extra camera
   *    was gone for everyone else for the rest of the call.
   *  - No transport (a rebuild in progress): wait, keeping the capture live.
   */
  useEffect(() => {
    const transport = sfuState.sendTransport
    for (const sourceId of [...configuredInputsRef.current.keys()]) {
      const producer = auxProducersRef.current.get(sourceId)
      if (!camOn) {
        if (producer || auxStreamsRef.current.has(sourceId)) suspendVideoInput(sourceId)
        continue
      }
      if (!transport) continue
      if (producer && !producer.closed) continue
      startVideoInput(sourceId).catch((err: unknown) => {
        // The device could not be opened again — most often because it has
        // been unplugged. An input that cannot capture is removed rather than
        // left as a dead tile.
        vclog('producer', 'additional video input could not restart — removed', {
          sourceId, error: (err as Error)?.message,
        })
        unpublishVideoInputRef.current(sourceId)
      })
    }
  }, [camOn, sfuState.sendTransport, startVideoInput, suspendVideoInput])

  /**
   * Drop an input whose camera is no longer plugged in.
   *
   * The track's own `ended` covers a camera unplugged while capturing. It does
   * not cover a camera unplugged while its input is off (no track to end), nor
   * a track that had already been stopped by something else — which is how an
   * input stayed on screen as a black tile after its camera was removed.
   */
  useEffect(() => {
    const md = navigator.mediaDevices
    if (!md?.addEventListener) return
    const onChange = async () => {
      if (configuredInputsRef.current.size === 0) return
      const devices = await md.enumerateDevices().catch(() => null)
      if (!devices) return
      const cams = devices.filter((d) => d.kind === 'videoinput')
      // Enumeration without permission returns blank ids; that is not every
      // camera leaving.
      if (!cams.some((d) => !!d.deviceId)) return
      const present = new Set(cams.map((d) => d.deviceId))
      for (const [sourceId, cfg] of [...configuredInputsRef.current]) {
        if (present.has(cfg.deviceId)) continue
        vclog('producer', 'additional video input camera unplugged — removed', { sourceId, label: cfg.label })
        unpublishVideoInputRef.current(sourceId)
      }
    }
    md.addEventListener('devicechange', onChange)
    return () => md.removeEventListener('devicechange', onChange)
  }, [])

  return {
    connected,
    sfuReady: sfuState.ready,
    localStream,
    remotePeers,
    peerId,
    activeSpeakerId,
    speakingId,
    subscribe,
    sendEvent,
    request,
    channelJoined,
    roomId,
    error: sfuState.error,
    publishScreenShare,
    unpublishScreenShare,
    screenSharePending,
    localVideoInputs,
    publishVideoInput,
    unpublishVideoInput,
    unpublishAllVideoInputs,
  }
}
